import { applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import { routerMiddleware } from 'connected-react-router';
import { settingsMiddlewares } from '@domain/settings/settings.state';
import { createAxiosMiddleware } from '@shared/axios';
import { intercomScriptMiddleware } from '@domain/analytics/intercom/intercom-script-middleware';
import { authMiddlewares } from '@domain/auth/auth-state';
import { integrationsMiddlewares } from '@domain/integrations/integrations.state';
import { updateObjectSelectedMiddleware } from '@domain/object-selected/object-selected.middleware';
import { alertsMiddleware, updateRouterLocationMiddleware } from '@domain/alert/alert.middleware';
import { cardsMiddlewares } from '@domain/cards/cards.state';
import { dashboardMiddlewares } from '@domain/dashboard/dashboard.state';
import {
    REDUX_WORKFLOW_FILTERS,
    REDUX_WORKFLOW_SEARCH,
} from '@shared/constants/localstorage.constants';
import { closeCardModalWithTimeoutMiddleware } from '@domain/cards/interface/card-interface.middleware';
import { tagGroupSegmentedMiddleware } from '@domain/segments/tag-groups-segmented/tag-groups-segmented.middleware';
import { teamMembersScreenMiddlewares } from '@domain/team/management/team.screen.middlewares';
import { partnershipsMiddlewares } from '@domain/partnerships/partnerships.state';
import { hotspotMiddlewares } from '@domain/dashboard/hotspot/hot-spot.middleware';
import { comparisonCampaignsMiddleware } from '@domain/comparison-campaigns/comparison-campaigns.middlewares';
import { segmentsModalMiddleware } from '@domain/segments/segments-modal/segments-modal.middlewares';
import { budgetManagerMiddleware } from '@domain/dashboard/budget-manager';
import { weglotMiddleware } from '@shared/translation/weglot/weglot.middleware';
import { budgetManagerMiddlewares } from '@domain/budget-manager/budget-manager.middleware';
import { campaignsInfoMiddleware } from '@domain/campaigns-info/campaigns-info.middleware';
import { omnichannelBudgetManagerMiddlewares } from '@domain/omnichannel-budget-manager/omnichannel-budget-manager.middlewares';
import { toastMiddleWares } from './shared/toasts/toasts.middlewares';
import { clustersMiddleware } from './domain/dashboard/cluster/cluster.middleware';
import { internalRoutesMiddleware } from './domain/router/router.middlewares';
import { loggedUserMiddlewares } from './domain/logged-user/logged-user-middlewares';
import { analyticsMiddleware } from './domain/analytics/product/analytics.middleware';
import { segmentsMiddleware } from './domain/segments/segments.middleware';
import { platformMiddlewares } from './domain/platform';
import { userConfigMiddlewares } from './domain/user-config';
import { campaignsMiddlewares } from './domain/campaigns';
import createReducer from './reducers';

const configureStore = (initialState = {}, history) => {
    const middlewares = [
        routerMiddleware(history),
        thunk,
        updateObjectSelectedMiddleware,
        createAxiosMiddleware(),
        updateRouterLocationMiddleware,
        alertsMiddleware,
        ...partnershipsMiddlewares,
        clustersMiddleware,
        toastMiddleWares,
        ...authMiddlewares,
        ...integrationsMiddlewares,
        intercomScriptMiddleware,
        loggedUserMiddlewares,
        internalRoutesMiddleware,
        ...analyticsMiddleware,
        segmentsMiddleware,
        segmentsModalMiddleware,
        tagGroupSegmentedMiddleware,
        ...settingsMiddlewares,
        ...cardsMiddlewares,
        ...dashboardMiddlewares,
        ...platformMiddlewares,
        budgetManagerMiddleware,
        userConfigMiddlewares,
        closeCardModalWithTimeoutMiddleware,
        comparisonCampaignsMiddleware,
        teamMembersScreenMiddlewares,
        hotspotMiddlewares,
        ...campaignsMiddlewares,
        weglotMiddleware,
        ...budgetManagerMiddlewares,
        campaignsInfoMiddleware,
        ...omnichannelBudgetManagerMiddlewares,
    ];

    const enhancers = [applyMiddleware(...middlewares)];
    const composeEnhancers = composeWithDevTools({});

    const store = createStore(
        createReducer(history),
        initialState,
        composeEnhancers(...enhancers),
    );

    store.subscribe(() => {
        // persist your state
        const storeState = store.getState();
        const { workflow } = storeState;
        const { filters, search } = workflow;

        localStorage.setItem(REDUX_WORKFLOW_FILTERS, JSON.stringify(filters));
        localStorage.setItem(REDUX_WORKFLOW_SEARCH, JSON.stringify(search));
    });

    return store;
};

export default configureStore;
